import {
  Drawer as MuiDrawer,
  Box,
  Typography,
  Fab,
  Modal,
  IconButton,
  Button,
} from "@mui/material";

import { styled, Theme, CSSObject } from "@mui/material/styles";
import FilterIcon from "../../assets/icons/filter.svg";
import OpenDrawerIcon from "../../assets/icons/openDrawer.svg";
import { ReactNode, useState } from "react";
import classes from "./Sidebar.module.scss";
import useIsMobile from "../../hooks/useIsMobile.hook";
import { Close, FilterList as FilterListIcon } from "@mui/icons-material";
import { Stack } from "@mui/system";

interface SidebarDrawerProps {
  open: boolean;
  onToggle: () => void;
  showApplyFilters?: boolean;
  applyFilters?: () => void;
  children: ReactNode;
}

const drawerWidth = 288;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  marginTop: "72px",
  zIndex: 7,
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  marginTop: "72px",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const SidebarDrawer: React.FC<SidebarDrawerProps> = ({
  open,
  onToggle,
  showApplyFilters,
  applyFilters,
  children,
}) => {
  const isMobile = useIsMobile();
  const [modalOpen, setModalOpen] = useState(false);

  const handleModalApply = () => {
    setModalOpen(false);
    if (applyFilters) applyFilters();
  };

  if (isMobile) {
    return (
      <>
        <Fab
          data-testid="fab-button"
          className={classes["fab-button"]}
          onClick={() => setModalOpen(true)}
        >
          <FilterListIcon className={classes["icon"]} />
        </Fab>

        <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
          <Box className={classes["filter-modal"]} data-testid="filter-modal">
            <Box className={classes["modal-header"]}>
              <Typography className={classes["modal-title"]}>
                Filters
              </Typography>
              <IconButton
                className={classes["close-button"]}
                onClick={() => setModalOpen(false)}
                data-testid="close-button"
              >
                <Close />
              </IconButton>
            </Box>
            <Box className={classes["modal-content"]}>{children}</Box>
            <Box className={classes["modal-footer"]}>
              <Button
                variant="contained"
                className={classes.applyBtn}
                onClick={handleModalApply}
                disabled={!showApplyFilters}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Modal>
      </>
    );
  }

  return (
    <Drawer data-testid="drawer" variant="permanent" open={open}>
      <Box
        className={`${classes["drawer-container"]} ${
          open ? classes["expanded"] : ""
        }`}
      >
        <Box className={classes["drawer-header"]}>
          {open && (
            <Typography
              className={classes["drawer-header-text"]}
              data-testid="filter-text"
            >
              Filters
            </Typography>
          )}
          <img
            className={classes["drawer-header-filter-icon"]}
            data-testid="filter-icon"
            src={FilterIcon}
            alt="Filter"
            onClick={onToggle}
          />
        </Box>
        <Stack className={classes.drawerBody}>
          <Box className={classes["drawer-content"]}>{children}</Box>
          <Box className={classes["drawer-footer"]}>
            {!open ? (
              <img
                data-testid="open-drawer-icon"
                className={classes["drawer-footer-icon"]}
                src={OpenDrawerIcon}
                alt="Toggle"
                onClick={onToggle}
              />
            ) : (
              <Button
                variant="contained"
                className={classes.applyBtn}
                onClick={applyFilters}
                disabled={!showApplyFilters}
              >
                Apply
              </Button>
            )}
          </Box>
        </Stack>
      </Box>
    </Drawer>
  );
};

export default SidebarDrawer;
